/**
 * Hybrid token storage (plan §7):
 *  - refresh token (+ bookkeeping keys) → localStorage, so sessions survive
 *    reloads/restarts up to the refresh-token validity
 *  - id/access tokens → memory only, so an XSS storage dump never yields a
 *    live token (only the revocable refresh token)
 *
 * Keys keep the `CognitoIdentityServiceProvider.<clientId>.*` prefix so
 * clear() also removes anything left behind by the pre-hosted-UI SDK login.
 */
const PERSISTED_SUFFIXES = ['.refreshToken', '.clockDrift'];
const PERSISTED_KEYS = ['LastAuthUser'];

function shouldPersist(key: string): boolean {
  return (
    PERSISTED_SUFFIXES.some((s) => key.endsWith(s)) || PERSISTED_KEYS.some((k) => key.endsWith(k))
  );
}

const memory = new Map<string, string>();

export const hybridStorage = {
  setItem(key: string, value: string): string {
    if (shouldPersist(key)) {
      window.localStorage.setItem(key, value);
    } else {
      memory.set(key, value);
    }
    return value;
  },

  getItem(key: string): string | null {
    return memory.get(key) ?? window.localStorage.getItem(key);
  },

  removeItem(key: string): void {
    memory.delete(key);
    window.localStorage.removeItem(key);
  },

  clear(): void {
    memory.clear();
    // Only clear Cognito-owned keys from localStorage, not unrelated app state
    const cognitoKeys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('CognitoIdentityServiceProvider')) cognitoKeys.push(k);
    }
    cognitoKeys.forEach((k) => window.localStorage.removeItem(k));
  },
};
